import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import classNames from 'classnames';
import {push} from 'react-router-redux';
import ReactTooltip from 'react-tooltip';

import {reverse} from 'utils/urls';
import {
  REVENUE_REPORTING_URL,
  LOOKER_REPORTING_URL,
  OCCUPANCY_REPORTING_URL,
  INVOICE_REPORTING_URL,
  AVAILABILITY_URL,
  ALLOCATIONS_URL,
  BOOKING_SET_LIST_URL,
  BOOKING_LIST_URL,
  BOOKING_TRACKER_URL,
  FRONT_DESK_URL,
  PAYMENTS_LIST_URL,
  CREDIT_NOTES_LIST_URL,
  MEMBER_LIST_URL,
  NOTES_LIST_URL,
  ONE_OFF_CHARGE_REASONS_URL
} from 'config/urls';

import {PERMISSIONS, requiresPermission} from 'ducks/auth';

import IconButton from 'components/IconButton';
import ImageSwitch from 'components/ImageSwitch';
import Tooltip from 'components/Tooltip';
import {IconButtonAndText} from './components';

import downArrow from './assets/downArrow.svg';
import frontDeskIcon from './assets/frontDesk.svg';
import frontDeskActiveIcon from './assets/frontDeskActive.svg';
import bookingSetsIcon from './assets/bookingSets.svg';
import bookingSetsActiveIcon from './assets/bookingSetsActive.svg';
import bookingsIcon from './assets/bookings.svg';
import bookingsActiveIcon from './assets/bookingsActive.svg';
import allocationsIcon from './assets/allocations.svg';
import allocationsActiveIcon from './assets/allocationsActive.svg';
import roomsIcon from './assets/rooms.svg';
import roomsActiveIcon from './assets/roomsActive.svg';
import invoicesIcon from './assets/invoices.svg';
import invoicesActiveIcon from './assets/invoicesActive.svg';
import paymentsIcon from './assets/payments.svg';
import paymentsActiveIcon from './assets/paymentsActive.svg';
import revenueIcon from './assets/revenue.svg';
import lookerIcon from './assets/looker.svg';
import lookerActiveIcon from './assets/lookerActive.svg';
import revenueActiveIcon from './assets/revenueActive.svg';
import occupancyIcon from './assets/occupancy.svg';
import occupancyActiveIcon from './assets/occupancyActive.svg';
import membersListIcon from './assets/memberList.svg';
import membersListActiveIcon from './assets/memberListActive.svg';
import notesIcon from './assets/notes.svg';
import notesActiveIcon from './assets/notesActive.svg';
import sideBarToggleIcon from './assets/sideBarToggle.svg';
import sideBarToggleOpenIcon from './assets/sideBarToggleOpen.svg';

import {getMenuOpenState} from 'ducks/page';

import './styles.css';

const TOOLTIP_NAME = 'leftSideBarTooltip';

interface PassedProps {
  activeTab?: string;
  leftSideBarOpened: boolean;
  locationRef: string;
  className?: string;
  toggleLeftSideBar: () => void;
}

type LeftSideBarProps = PassedProps &
  ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

class LeftSideBar extends React.Component<LeftSideBarProps> {
  state = {
    openedSections: ['Operations', 'Finance', 'Reporting']
  };

  toggleLeftSidebar = () => {
    this.props.toggleLeftSideBar();
  };

  componentDidUpdate(prevProps) {
    if (
      prevProps.menuOpenStateFromStore !== this.props.menuOpenStateFromStore
    ) {
      ReactTooltip.rebuild();
    }
  }

  getSideMenu = () => {
    const {locationRef} = this.props;

    return [
      {
        namespace: 'Operations',
        buttons: [
          {
            ref: 'frontDesk',
            label: 'Front Desk',
            image: frontDeskIcon,
            activeImage: frontDeskActiveIcon,
            url: reverse(FRONT_DESK_URL, {locationRef}),
            permissionKwargs: {
              permission: PERMISSIONS.VIEW_FRONT_DESK_DASHBOARD,
              locationRef
            }
          },
          {
            ref: 'bookingSets',
            label: 'Booking Sets',
            image: bookingSetsIcon,
            activeImage: bookingSetsActiveIcon,
            url: reverse(BOOKING_SET_LIST_URL, {locationRef})
          },
          {
            ref: 'bookings',
            label: 'Bookings',
            image: bookingsIcon,
            activeImage: bookingsActiveIcon,
            url: reverse(BOOKING_LIST_URL, {locationRef})
          },
          {
            ref: 'bookingTracker',
            label: 'Bookings Tracker',
            image: bookingsIcon,
            activeImage: bookingsActiveIcon,
            url: reverse(BOOKING_TRACKER_URL, {locationRef})
          },
          {
            ref: 'allocations',
            label: 'Allocations',
            image: allocationsIcon,
            activeImage: allocationsActiveIcon,
            url: reverse(ALLOCATIONS_URL, {locationRef}),
            permissionKwargs: {
              permission: PERMISSIONS.VIEW_ALLOCATIONS,
              locationRef
            }
          },
          {
            ref: 'availability',
            label: 'Availability',
            image: roomsIcon,
            activeImage: roomsActiveIcon,
            url: reverse(AVAILABILITY_URL, {locationRef}),
            permissionKwargs: {
              permission: PERMISSIONS.VIEW_ROOMS_DASHBOARD,
              locationRef
            }
          },
          {
            ref: 'membersList',
            label: 'Members List',
            image: membersListIcon,
            activeImage: membersListActiveIcon,
            url: reverse(MEMBER_LIST_URL, {locationRef}),
            permissionKwargs: {
              permission: PERMISSIONS.VIEW_MEMBERS_LIST,
              locationRef
            }
          },
          {
            ref: 'notesList',
            label: 'Notes List',
            image: notesIcon,
            activeImage: notesActiveIcon,
            url: reverse(NOTES_LIST_URL, {locationRef}),
            permissionKwargs: {
              permission: PERMISSIONS.VIEW_NOTES_LIST,
              locationRef
            }
          },
          {
            ref: 'chargeReasons',
            label: 'Charge Reasons',
            image: invoicesIcon,
            activeImage: invoicesActiveIcon,
            url: reverse(ONE_OFF_CHARGE_REASONS_URL, {locationRef}),
            permissionKwargs: {
              permission: PERMISSIONS.VIEW_ONE_OFF_CHARGE_REASONS,
              locationRef
            }
          }
        ]
      },
      {
        namespace: 'Finance',
        buttons: [
          {
            ref: 'invoices',
            label: 'Invoices',
            image: invoicesIcon,
            activeImage: invoicesActiveIcon,
            url: reverse(INVOICE_REPORTING_URL, {locationRef}),
            permissionKwargs: {
              permission: PERMISSIONS.VIEW_INVOICE_REPORTING,
              locationRef
            }
          },
          {
            ref: 'payments',
            label: 'Payments',
            image: paymentsIcon,
            activeImage: paymentsActiveIcon,
            url: reverse(PAYMENTS_LIST_URL, {locationRef}),
            permissionKwargs: {
              permission: PERMISSIONS.VIEW_PAYMENTS_LIST,
              locationRef
            }
          },
          {
            ref: 'creditNotes',
            label: 'Credit Notes',
            image: invoicesIcon,
            activeImage: invoicesActiveIcon,
            url: reverse(CREDIT_NOTES_LIST_URL, {locationRef}),
            permissionKwargs: {
              permission: PERMISSIONS.VIEW_CREDIT_NOTES_LIST,
              locationRef
            }
          }
        ]
      },
      {
        namespace: 'Reporting',
        buttons: [
          {
            ref: 'LookerRevenueReporting',
            label: 'Looker',
            image: lookerIcon,
            activeImage: lookerActiveIcon,
            url: reverse(LOOKER_REPORTING_URL, {locationRef}),
            permissionKwargs: {
              permission: PERMISSIONS.VIEW_LOOKER_DASHBOARD,
              locationRef
            }
          },
          {
            ref: 'revenueReporting',
            label: 'Revenue (legacy)',
            image: revenueIcon,
            activeImage: revenueActiveIcon,
            url: reverse(REVENUE_REPORTING_URL, {locationRef}),
            permissionKwargs: {
              permission: PERMISSIONS.VIEW_REVENUE_REPORTING,
              locationRef
            }
          },
          {
            ref: 'occupancy',
            label: 'Occupancy (legacy)',
            image: occupancyIcon,
            activeImage: occupancyActiveIcon,
            url: reverse(OCCUPANCY_REPORTING_URL, {locationRef}),
            permissionKwargs: {
              permission: PERMISSIONS.VIEW_OCCUPANCY_DASHBOARD,
              locationRef
            }
          }
        ]
      }
    ];
  };

  toggleSection = (sectionName: string) => {
    this.setState(
      {
        openedSections: _.xor(this.state.openedSections, [sectionName])
      },
      () => {
        ReactTooltip.rebuild();
      }
    );
  };

  isSectionActive = (sectionName: string) => {
    return _.includes(this.state.openedSections, sectionName);
  };

  handleClick = button => {
    const redirect = () => this.props.push(button.url);

    if (_.isEmpty(button.permissionKwargs)) {
      redirect();
    } else {
      this.props.requiresPermission({...button.permissionKwargs}, redirect);
    }
  };

  render() {
    const {leftSideBarOpened, activeTab, className} = this.props;
    const sideMenu = this.getSideMenu();

    return (
      <div
        className={classNames(
          className,
          'LeftSideBar',
          'colab-row-space-between',
          {
            'LeftSideBar--closed': !leftSideBarOpened
          }
        )}>
        <div>
          {sideMenu.map(section => (
            <div
              className={classNames('LeftSideBar__Section', {
                'LeftSideBar__Section--active': this.isSectionActive(
                  section.namespace
                )
              })}
              key={section.namespace}>
              {leftSideBarOpened && (
                <div className="LeftSideBar__Section__heading colab-row-space-between colab-centered-v">
                  <div className="colab-bold">{section.namespace}</div>
                  <IconButton
                    src={downArrow}
                    alt="Hide tab"
                    onClick={() => this.toggleSection(section.namespace)}
                  />
                </div>
              )}
              <div className="LeftSideBar__Section__content">
                {!leftSideBarOpened &&
                  section.buttons.map(button => (
                    <div
                      data-tip={button.label}
                      data-for={TOOLTIP_NAME}
                      key={button.ref}>
                      <IconButton
                        src={button.image}
                        srcActive={button.activeImage}
                        alt={button.label}
                        active={button.ref === activeTab}
                        activeBlue={true}
                        onClick={() => this.handleClick(button)}
                      />
                    </div>
                  ))}
                {leftSideBarOpened &&
                  section.buttons.map(button => (
                    <IconButtonAndText
                      key={button.ref}
                      src={button.image}
                      srcActive={button.activeImage}
                      alt={button.label}
                      text={button.label}
                      active={button.ref === activeTab}
                      onClick={() => this.handleClick(button)}
                    />
                  ))}
              </div>
            </div>
          ))}
        </div>
        <div className="LeftSideBar__toggleButton">
          <ImageSwitch
            src={sideBarToggleIcon}
            srcActive={sideBarToggleOpenIcon}
            alt="Left Side bar toggle"
            active={leftSideBarOpened}
            onClick={this.toggleLeftSidebar}
            className="colab-clickable"
          />
        </div>
        <Tooltip
          place="right"
          delayShow={300}
          effect="solid"
          id={TOOLTIP_NAME}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  menuOpenStateFromStore: getMenuOpenState(state)
});

const mapDispatchToProps = {
  push,
  requiresPermission
};

export default connect(mapStateToProps, mapDispatchToProps)(LeftSideBar);
